import { FC, useEffect } from 'react';
import useBackendLanguage from '@hooks/useBackendLanguage';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import PrivateRoutes from '@js/PrivateRoutes';
import log, { LogContext } from '@utils/logging';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import { disableUserSelectionAtom } from '@js/store/appAtoms';
import Sam from '@components/features/sam/Sam';
import useAnalytics from '@hooks/useAnalytics';
import useAuthentication from '@hooks/useAuthentication';
import usePageDescription from '@hooks/usePageDescription';

const App: FC = () => {
    const { getAccessToken, decodeAccessToken } = useAuthentication();
    const token = getAccessToken();
    const [cookies, setCookie, removeCookie] = useCookies();
    const { t } = useTranslation();
    const disableUserSelection = useAtomValue(disableUserSelectionAtom);
    useBackendLanguage();
    useAnalytics();
    usePageDescription(t('meta.description'));

    const storeClientVersion = (newToken: string) => {
        const decodedToken = decodeAccessToken(newToken);
        // Remove clientversion cookie and refresh page when clientversion in token is empty.
        if (!decodedToken?.clientversion && cookies?.clientversion) {
            log({
                context: LogContext.User,
                title: 'Removed clientVersion cookie',
                reason: 'No clientVersion available in token',
                data: {
                    removedClientVersion: cookies.clientversion,
                },
            });
            removeCookie('clientversion');
            window.location.reload();
            return;
        }

        // Do nothing when clientversion from cookie is the same as clientversion from token. Everything is fine.
        if (!decodedToken?.clientversion || cookies?.clientversion === decodedToken?.clientversion) {
            return;
        }

        // Update cookie and refresh page when clientversion from token differs from clientversion from cookie.
        log({
            context: LogContext.User,
            title: 'Update clientVersion cookie',
            reason: 'clientVersion from token differs from clientVersion from cookie',
            data: {
                oldClientVersion: cookies.clientversion,
                newClientVersion: decodedToken.clientversion,
            },
        });
        setCookie('clientversion', decodedToken.clientversion, {
            path: '/',
        });
        window.location.reload();
    };

    useEffect(() => {
        if (!token) {
            return;
        }

        storeClientVersion(token);
    }, [token]);

    return (
        <div
            className={classNames('app', {
                'app--disableUserSelection': disableUserSelection,
            })}
        >
            <PrivateRoutes />
            <div className="modals" />
            <Sam />
        </div>
    );
};

export default App;
